import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PLATFORM_IMAGES } from "@/constants/platformImages";

export default function FramerIntegration() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/settings', { replace: true })}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-background border border-border/30">
              <img src={PLATFORM_IMAGES.framer} alt="Framer" className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-foreground">Framer Integration</h1>
              <p className="text-muted-foreground mt-2">
                Connect your Framer site to publish SEO-optimized articles directly to your Framer CMS
              </p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <Card className="mb-6 border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-600 dark:text-yellow-500">
              <BookOpen className="h-5 w-5" />
              Important Notice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              <strong className="text-foreground">Framer does not currently provide a public REST API with API keys</strong> for direct integration like Webflow or Shopify. Framer uses OAuth authentication and a Plugin API system.
            </p>
            <p className="text-muted-foreground mb-4">
              We're working on implementing OAuth-based integration for Framer. In the meantime, you can:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-muted-foreground">
              <li>Use our other supported platforms (WordPress, Shopify, Webflow) for direct publishing</li>
              <li>Manually copy and paste your generated articles into Framer</li>
              <li>Check back soon for OAuth-based Framer integration</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              For more information about Framer's integration options, visit their <a href="https://www.framer.com/developers/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">developer documentation</a>.
            </p>
          </CardContent>
        </Card>

        {/* Prerequisites */}
        <Card className="mb-6 opacity-60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Prerequisites (Coming Soon)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Once OAuth integration is available, you'll need:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-muted-foreground">
              <li>A Framer account with site access</li>
              <li>OAuth access enabled for your Framer site</li>
              <li>A CMS collection set up in your Framer project</li>
            </ul>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <Card className="mb-6 opacity-60">
          <CardHeader>
            <CardTitle>Step 1: OAuth Setup (Coming Soon)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Framer integration will use OAuth authentication instead of API keys:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to your Framer account</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Go to Account Settings → API Access</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Navigate to your account settings and find the API Access section.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">Click "Create API Key" or "Generate Token"</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Give your API key a name (e.g., "SEORO Integration")</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">5</Badge>
                <div>
                  <p className="font-medium">Select the following permissions:</p>
                  <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                    <li className="text-muted-foreground">CMS: Read and write</li>
                    <li className="text-muted-foreground">Site: Read and write</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">6</Badge>
                <div>
                  <p className="font-medium">Copy the API key and save it somewhere safe</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    <strong>Note:</strong> This key is only shown once. Make sure to copy it immediately.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">7</Badge>
                <div>
                  <p className="font-medium">Get your Site ID</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    In your Framer dashboard, go to your site settings. The Site ID is displayed in the site settings or can be found in the URL when viewing your site in the Framer editor.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">8</Badge>
                <div>
                  <p className="font-medium">Get your Collection ID</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    In your Framer CMS, open your blog posts collection. The Collection ID can be found in the collection settings or in the URL when editing the collection.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 2: Connect SEORO to Framer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to SEORO and navigate to Settings → Integrations</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Under Publishing Platforms, click the Connect button next to Framer</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">In the popup, enter your Framer API Key</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Click "Verify" to fetch your sites</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">5</Badge>
                <div>
                  <p className="font-medium">Select your Framer site from the dropdown</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">6</Badge>
                <div>
                  <p className="font-medium">Select your CMS collection (blog posts collection is recommended)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">7</Badge>
                <div>
                  <p className="font-medium">Click "Connect" to complete the setup</p>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg mt-4">
              <p className="font-medium mb-2">API Key Permissions</p>
              <p className="text-sm text-muted-foreground">
                Make sure your API key has the following permissions:
              </p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1 text-sm text-muted-foreground">
                <li>CMS: Read and write</li>
                <li>Site: Read and write</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Step 3: You Are Connected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You'll see a success message confirming your Framer integration.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              If you face any issues, contact support using the sidebar chat within the app.
            </p>
          </CardContent>
        </Card>

        {/* Publishing Workflow */}
        <Card>
          <CardHeader>
            <CardTitle>Publishing Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              • Articles published from SEORO will be created as items in your Framer CMS collection.
            </p>
            <p className="text-muted-foreground">
              • Make sure your Framer CMS collection has the following fields configured:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-muted-foreground">
              <li>Title (text field)</li>
              <li>Content/Body (rich text or HTML field)</li>
              <li>Slug (text field, optional but recommended)</li>
              <li>Excerpt/Summary (text field, optional)</li>
              <li>Featured Image (image field, optional)</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              • After publishing, you may need to publish your Framer site manually to make the content live.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

